import { useCallback } from "react";

import { useGridContext } from "../../hooks/useEvaluateGridContext";

import { ReportColumn } from "@/types/evaluate";

export const useColumnPinning = () => {
  const { gridApiRef } = useGridContext();

  const handlePinColumn = useCallback(
    (column: ReportColumn, pinDirection: "left" | "right" | null) => {
      const gridApi = gridApiRef?.current;
      if (!gridApi) {
        return;
      }

      const colId = `col_${column.id}`;

      if (pinDirection !== null) {
        gridApi.applyColumnState({
          state: [{ colId, pinned: pinDirection }],
        });
        return;
      }

      const currentState = gridApi.getColumnState();
      const target = currentState.find(s => s.colId === colId);
      if (!target) {
        return;
      }

      gridApi.applyColumnState({
        state: currentState.map(s => (s.colId === colId ? { ...s, pinned: null } : s)),
      });
    },
    [gridApiRef],
  );

  const handlePinLeft = useCallback(
    (column: ReportColumn) => {
      handlePinColumn(column, "left");
    },
    [handlePinColumn],
  );

  const handlePinRight = useCallback(
    (column: ReportColumn) => {
      handlePinColumn(column, "right");
    },
    [handlePinColumn],
  );

  const handleUnpin = useCallback(
    (column: ReportColumn) => {
      handlePinColumn(column, null);
    },
    [handlePinColumn],
  );

  const isColumnPinned = useCallback(
    (column: ReportColumn, direction?: "left" | "right") => {
      const gridApi = gridApiRef?.current;

      if (!gridApi) {
        return false;
      }

      try {
        const columnState = gridApi.getColumnState();
        const columnStateItem = columnState.find(state => state.colId === `col_${column.id}`);

        if (!columnStateItem) {
          return false;
        }

        const pinnedState = columnStateItem.pinned;

        if (direction) {
          return pinnedState === direction;
        }

        return !!pinnedState;
      } catch {
        return false;
      }
    },
    [gridApiRef],
  );

  return {
    handlePinLeft,
    handlePinRight,
    handleUnpin,
    isColumnPinned,
  };
};
